import React from "react";
import { Link } from "react-router-dom";
import SectionHeader from "./SectionHeader";
import MediaHeader from "./MediaHeader";
import PortalEntryLink from "../portal/PortalEntryLink";
import Banner1 from "../image/Banner1.jpg";
import Banner2 from "../image/Banner2.jpg";
import Banner3 from "../image/Banner3.jpg";
import Banner4 from "../image/Banner4.jpg";
import Kd1 from "../image/kd1.jpg";

const visualStory = [
  {
    src: Banner1,
    alt: "S-TEE featured moment one",
    tag: "Campus Welcome",
  },
  {
    src: Banner2,
    alt: "S-TEE featured moment two",
    tag: "Classroom Life",
  },
  {
    src: Banner3,
    alt: "S-TEE featured moment three",
    tag: "School Experience",
  },
  {
    src: Banner4,
    alt: "S-TEE featured moment four",
    tag: "Community",
  },
  {
    src: Kd1,
    alt: "S-TEE featured moment five",
    tag: "Student Highlights",
  },
];

const normalizeCopy = (value) => String(value || "").replace(/\*\*/g, "").trim();
const isApplicantPortalTarget = (actionOrItem, linkTarget) =>
  actionOrItem?.portalRole === "applicant" || linkTarget === "/admissions/apply";

const TextGroup = ({
  text,
  className = "text-base leading-8 text-slate-600",
  containerClassName = "space-y-4",
}) => {
  const normalized = normalizeCopy(text);

  if (!normalized) {
    return null;
  }

  const paragraphs = normalized
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

  return (
    <div className={containerClassName}>
      {paragraphs.map((paragraph, index) => (
        <p key={`${paragraph.slice(0, 24)}-${index}`} className={`${className} whitespace-pre-line`}>
          {paragraph}
        </p>
      ))}
    </div>
  );
};

const ActionLink = ({ action, variant = "primary", className = "" }) => {
  const linkTarget = action?.href || action?.path;

  if (!linkTarget || !action?.label) {
    return null;
  }

  const variantClassNames = {
    primary:
      "bg-[#102046] text-white shadow-[0_24px_48px_-30px_rgba(16,32,70,0.7)] hover:bg-[#17305e]",
    secondary:
      "bg-[#d4a448] text-slate-900 shadow-[0_24px_48px_-30px_rgba(212,164,72,0.75)] hover:bg-[#e2b65f]",
    ghost:
      "border border-[#d7cfbf] bg-white text-slate-700 hover:border-[#102046] hover:text-[#102046]",
  };

  const sharedClassName = `inline-flex items-center justify-center rounded-full px-6 py-3 text-xs font-bold uppercase tracking-[0.18em] transition ${variantClassNames[variant] || variantClassNames.primary} ${className}`;
  const isExternal = /^(https?:|mailto:|tel:)/.test(linkTarget);

  if (isApplicantPortalTarget(action, linkTarget)) {
    return (
      <PortalEntryLink role="applicant" className={sharedClassName}>
        {action.label}
      </PortalEntryLink>
    );
  }

  if (isExternal) {
    return (
      <a
        href={linkTarget}
        className={sharedClassName}
        target={linkTarget.startsWith("http") ? "_blank" : undefined}
        rel={linkTarget.startsWith("http") ? "noopener noreferrer" : undefined}
      >
        {action.label}
      </a>
    );
  }

  return (
    <Link to={linkTarget} className={sharedClassName}>
      {action.label}
    </Link>
  );
};

const SidebarCards = ({ cards }) => {
  if (!cards?.length) {
    return null;
  }

  return (
    <div className="grid gap-5">
      {cards.map((card, index) => (
        <article
          key={`${card.title}-${index}`}
          className="rounded-[1.75rem] border border-[#eadfc8] bg-[#fffdfa] p-6 shadow-[0_20px_50px_-36px_rgba(15,23,42,0.2)]"
        >
          <h3 className="text-2xl text-slate-900">{card.title}</h3>
          {Array.isArray(card.items) && card.items.length ? (
            <div className="mt-4 space-y-3">
              {card.items.map((item, itemIndex) =>
                typeof item === "string" ? (
                  <p key={`${item.slice(0, 24)}-${itemIndex}`} className="text-sm leading-7 text-slate-600">
                    {item}
                  </p>
                ) : isApplicantPortalTarget(item, item.path) ? (
                  <PortalEntryLink
                    key={`${item.label}-${itemIndex}`}
                    role="applicant"
                    className="block rounded-2xl bg-[#f7f3ea] px-4 py-3 text-sm font-semibold text-[#102046] transition hover:bg-[#efe6d2]"
                  >
                    {item.label}
                  </PortalEntryLink>
                ) : (
                  <Link
                    key={`${item.label}-${itemIndex}`}
                    to={item.path}
                    className="block rounded-2xl bg-[#f7f3ea] px-4 py-3 text-sm font-semibold text-[#102046] transition hover:bg-[#efe6d2]"
                  >
                    {item.label}
                  </Link>
                )
              )}
            </div>
          ) : null}
          {card.note ? (
            <p className="mt-4 text-sm leading-7 text-slate-500">{card.note}</p>
          ) : null}
          {card.ctaLabel && (card.ctaPath || card.ctaHref) ? (
            <ActionLink
              action={{ label: card.ctaLabel, path: card.ctaPath, href: card.ctaHref }}
              variant="secondary"
              className="mt-5"
            />
          ) : null}
        </article>
      ))}
    </div>
  );
};

const DirectoryCards = ({ section }) => {
  if (!section?.items?.length) {
    return null;
  }

  return (
    <section className="bg-gradient-to-b from-[#102046] to-[#1a3a6e] px-6 py-16 md:px-12 lg:px-20">
      <div className="mx-auto max-w-6xl">
        <SectionHeader label={section.label} title={section.title} invert />
        <div className="mt-10 grid gap-6 lg:grid-cols-4">
          {section.items.map((item, index) => (
            <article
              key={`${item.title}-${index}`}
              className="rounded-[1.75rem] border border-[#2a4a7e] bg-[#17305e] p-6 shadow-[0_20px_50px_-36px_rgba(15,23,42,0.2)] transition hover:bg-[#1e3f7a]"
            >
              {item.image ? (
                <div className="mb-5 overflow-hidden rounded-[1.2rem]">
                  <img
                    src={item.image}
                    alt={item.imageAlt || item.title}
                    className="w-full h-auto"
                  />
                </div>
              ) : null}
              <h3 className="text-2xl text-white">{item.title}</h3>
              <div className="mt-4 space-y-3">
                {item.lines?.map((line, lineIndex) => (
                  <p key={`${line.slice(0, 24)}-${lineIndex}`} className="text-sm leading-7 text-slate-200">
                    {line}
                  </p>
                ))}
              </div>
              {item.ctaLabel && (item.ctaPath || item.ctaHref) ? (
                <ActionLink
                  action={{ label: item.ctaLabel, path: item.ctaPath, href: item.ctaHref }}
                  variant="ghost"
                  className="mt-5"
                />
              ) : null}
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

const ChecklistSection = ({ section }) => {
  if (!section?.items?.length) {
    return null;
  }

  return (
    <section className="bg-[#fbf7ef] px-6 py-16 md:px-12 lg:px-20">
      <div className="mx-auto max-w-6xl rounded-[2rem] border border-[#eadfc8] bg-white p-8 shadow-[0_26px_70px_-44px_rgba(15,23,42,0.24)] md:p-10">
        <SectionHeader label={section.label} title={section.title} />
        {section.text ? (
          <TextGroup text={section.text} className="text-base leading-8 text-slate-600" />
        ) : null}
        <div className="mt-8 grid gap-4 md:grid-cols-2">
          {section.items.map((item, index) => (
            <div
              key={`${item.slice(0, 32)}-${index}`}
              className="rounded-[1.5rem] border border-[#efe6d2] bg-[#fffdfa] px-5 py-5"
            >
              <div className="flex items-start gap-4">
                <div className="mt-1 flex h-8 w-8 items-center justify-center rounded-full bg-[#102046] text-xs font-semibold text-white">
                  {index + 1}
                </div>
                <p className="text-sm leading-7 text-slate-700">{item}</p>
              </div>
            </div>
          ))}
        </div>
        {section.note ? (
          <p className="mt-6 text-sm leading-7 text-slate-500">{section.note}</p>
        ) : null}
      </div>
    </section>
  );
};

const TimelineSection = ({ section }) => {
  if (!section?.rows?.length) {
    return null;
  }

  return (
    <section className="px-6 py-16 md:px-12 lg:px-20">
      <div className="mx-auto max-w-6xl">
        <SectionHeader label={section.label} title={section.title} />
        <div className="mt-10 overflow-hidden rounded-[2rem] border border-[#eadfc8] bg-[#fffdfa] shadow-[0_24px_60px_-40px_rgba(15,23,42,0.22)]">
          <div className="grid grid-cols-3 border-b border-[#efe4cc] bg-[#102046] text-xs font-bold uppercase tracking-[0.16em] text-white">
            {(section.columns || ["Stage", "Starts", "Ends"]).map((column) => (
              <div key={column} className="px-5 py-4">
                {column}
              </div>
            ))}
          </div>
          {section.rows.map((row, index) => (
            <div
              key={`${row.label}-${index}`}
              className={`grid grid-cols-3 px-5 py-4 text-sm text-slate-700 ${index !== section.rows.length - 1 ? "border-b border-[#f1e8d7]" : ""}`}
            >
              <div className="font-semibold text-slate-900">{row.label}</div>
              <div>{row.start}</div>
              <div>{row.end}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const InfoPage = ({
  eyebrow,
  title,
  description,
  heroImage,
  heroImageAlt,
  stats,
  heroActions,
  focus,
  callout,
  cardsSection,
  statementSection,
  checklistSection,
  timelineSection,
  directorySection,
  faqSection,
  newsSection,
  stepsSection,
  gallerySection,
  leadershipSection,
  sidebarSection,
  footerCta,
}) => {
  const resolvedHeroActions = Array.isArray(heroActions) && heroActions.length
    ? heroActions
    : [
      callout?.ctaLabel && callout?.ctaPath
        ? { label: callout.ctaLabel, path: callout.ctaPath, variant: "secondary" }
        : null,
      footerCta?.ctaLabel && footerCta?.ctaPath
        ? { label: footerCta.ctaLabel, path: footerCta.ctaPath, variant: "ghost" }
        : null,
    ].filter(Boolean);

  return (
    <>
      <section className="relative overflow-hidden bg-[linear-gradient(180deg,#fffdfa_0%,#f8f3ea_48%,#ffffff_100%)] px-6 py-16 md:px-12 lg:px-20 lg:py-20">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(212,164,72,0.16),transparent_32%),radial-gradient(circle_at_bottom_right,rgba(31,121,140,0.12),transparent_28%)]" />
        <div className="relative mx-auto grid max-w-6xl gap-12 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
          <div>
            {eyebrow ? (
              <span className="inline-flex rounded-full bg-[#fff1cc] px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#8a6a33]">
                {eyebrow}
              </span>
            ) : null}
            <h1 className="mt-6 text-4xl leading-[0.98] text-slate-900 md:text-6xl">
              {title}
            </h1>
            <div className="mt-6 max-w-2xl">
              <TextGroup text={description} className="text-base leading-8 text-slate-600 md:text-lg" />
            </div>
            {resolvedHeroActions.length ? (
              <div className="mt-8 flex flex-wrap gap-4">
                {resolvedHeroActions.map((action, index) => (
                  <ActionLink
                    key={`${action.label}-${index}`}
                    action={action}
                    variant={action.variant || (index === 0 ? "primary" : "ghost")}
                  />
                ))}
              </div>
            ) : null}
            {stats?.length ? (
              <div className="mt-10 grid gap-4 sm:grid-cols-3">
                {stats.map((stat) => (
                  <div
                    key={`${stat.value}-${stat.label}`}
                    className="rounded-[1.6rem] border border-[#eadfc8] bg-white/80 px-5 py-5 shadow-[0_20px_46px_-36px_rgba(15,23,42,0.22)] backdrop-blur"
                  >
                    <p className="text-2xl text-slate-900">{stat.value}</p>
                    <p className="mt-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                      {stat.label}
                    </p>
                  </div>
                ))}
              </div>
            ) : null}
          </div>

          <div className="relative">
            <div className="absolute -inset-5 rounded-[2.4rem] bg-[radial-gradient(circle_at_top,rgba(212,164,72,0.24),transparent_55%)] blur-2xl" />
            <div className="relative overflow-hidden rounded-[2.4rem] border border-white/70 bg-white shadow-[0_34px_90px_-50px_rgba(15,23,42,0.45)]">
              <img
                src={heroImage}
                alt={heroImageAlt || title}
                className="h-[320px] w-full object-cover md:h-[520px]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#09101f]/72 via-[#102046]/18 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                <div className="inline-flex rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-white backdrop-blur-sm">
                  S-TEE Schools
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {focus || callout ? (
        <section className="px-6 py-16 md:px-12 lg:px-20">
          <div className="mx-auto grid max-w-6xl gap-6 lg:grid-cols-[1.1fr_0.9fr]">
            {focus ? (
              <article className="rounded-[2rem] border border-[#eadfc8] bg-[#fffdfa] p-8 shadow-[0_24px_70px_-42px_rgba(15,23,42,0.22)] md:p-10">
                <SectionHeader label={focus.label} title={focus.title} />
                <TextGroup text={focus.text} />
                {focus.list?.length ? (
                  <div className="mt-8 grid gap-4">
                    {focus.list.map((item, index) => (
                      <div
                        key={`${item.slice(0, 28)}-${index}`}
                        className="rounded-[1.4rem] bg-[#f7f3ea] px-5 py-4 text-sm leading-7 text-slate-700"
                      >
                        {item}
                      </div>
                    ))}
                  </div>
                ) : null}
              </article>
            ) : null}

            {callout ? (
              <aside className="rounded-[2rem] bg-[#102046] p-8 text-white shadow-[0_30px_80px_-48px_rgba(2,6,23,0.8)] md:p-10">
                <SectionHeader label={callout.label} title={callout.title} />
                <TextGroup
                  text={callout.text}
                  className="text-base leading-8 text-slate-200/85"
                />
                {callout.ctaLabel && (callout.ctaPath || callout.ctaHref) ? (
                  <ActionLink
                    action={{ label: callout.ctaLabel, path: callout.ctaPath, href: callout.ctaHref }}
                    variant="secondary"
                    className="mt-8"
                  />
                ) : null}
              </aside>
            ) : null}
          </div>
        </section>
      ) : null}

      {leadershipSection?.profiles?.length ? (
        <section className="px-6 py-16 md:px-12 lg:px-20">
          <div className="mx-auto grid max-w-6xl gap-10 xl:grid-cols-[1.3fr_0.7fr]">
            <div>
              <SectionHeader label={leadershipSection.label} title={leadershipSection.title} />
              <div className="mt-10 space-y-10">
                {leadershipSection.profiles.map((profile, index) => (
                  <article
                    key={`${profile.name}-${index}`}
                    className="grid gap-6 rounded-[2rem] border border-[#eadfc8] bg-[#fffdfa] p-6 shadow-[0_24px_70px_-42px_rgba(15,23,42,0.22)] md:p-8 lg:grid-cols-[0.42fr_0.58fr]"
                  >
                    <div className={`${profile.reverse ? "lg:order-2" : ""}`}>
                      <div className="overflow-hidden rounded-[1.8rem] bg-[#f7f3ea]">
                        {profile.image ? (
                          <img
                            src={profile.image}
                            alt={profile.imageAlt || profile.name}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="flex min-h-[320px] items-center justify-center bg-[#102046] text-4xl font-semibold text-white">
                            {profile.initials}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className={`${profile.reverse ? "lg:order-1" : ""}`}>
                      {profile.eyebrow ? (
                        <span className="inline-flex rounded-full bg-[#fff1cc] px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#8a6a33]">
                          {profile.eyebrow}
                        </span>
                      ) : null}
                      <h3 className="mt-5 text-3xl text-slate-900">{profile.name}</h3>
                      {profile.role ? (
                        <p className="mt-2 text-sm font-semibold uppercase tracking-[0.16em] text-slate-500">
                          {profile.role}
                        </p>
                      ) : null}
                      {profile.messageTitle ? (
                        <h4 className="mt-6 text-2xl text-[#102046]">{profile.messageTitle}</h4>
                      ) : null}
                      <div className="mt-5 space-y-4">
                        {profile.messageParagraphs?.map((paragraph, paragraphIndex) => (
                          <p
                            key={`${paragraph.slice(0, 28)}-${paragraphIndex}`}
                            className="text-sm leading-7 text-slate-600"
                          >
                            {paragraph}
                          </p>
                        ))}
                      </div>
                      {profile.profileTitle ? (
                        <h4 className="mt-8 text-2xl text-[#102046]">{profile.profileTitle}</h4>
                      ) : null}
                      {profile.profileParagraphs?.length ? (
                        <div className="mt-5 space-y-4">
                          {profile.profileParagraphs.map((paragraph, paragraphIndex) => (
                            <p
                              key={`${paragraph.slice(0, 28)}-${paragraphIndex}`}
                              className="text-sm leading-7 text-slate-600"
                            >
                              {paragraph}
                            </p>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  </article>
                ))}
              </div>
            </div>

            <aside className="xl:pt-20">
              <SidebarCards cards={sidebarSection?.cards} />
            </aside>
          </div>
        </section>
      ) : null}

      {cardsSection?.items?.length ? (
        <section className="bg-white px-6 py-16 md:px-12 lg:px-20">
          <div className="mx-auto max-w-6xl">
            <SectionHeader label={cardsSection.label} title={cardsSection.title} />
            <div className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
              {cardsSection.items.map((item, index) => (
                <article
                  key={`${item.title}-${index}`}
                  className="rounded-[1.8rem] border border-[#eadfc8] bg-[#fffdfa] p-6 shadow-[0_20px_56px_-40px_rgba(15,23,42,0.22)]"
                >
                  <div className="inline-flex rounded-full bg-[#102046] px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-white">
                    {String(index + 1).padStart(2, "0")}
                  </div>
                  <h3 className="mt-5 text-2xl text-slate-900">{item.title}</h3>
                  <TextGroup
                    text={item.text}
                    className="mt-4 text-sm leading-7 text-slate-600"
                    containerClassName="mt-4 space-y-3"
                  />
                </article>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {statementSection?.text || statementSection?.items?.length || statementSection?.note ? (
        <section className="px-6 py-16 md:px-12 lg:px-20">
          <div className="mx-auto max-w-6xl rounded-[2rem] border border-[#eadfc8] bg-[#f7f3ea] p-8 shadow-[0_26px_70px_-44px_rgba(15,23,42,0.2)] md:p-10">
            <SectionHeader label={statementSection.label} title={statementSection.title} />
            {statementSection.text ? <TextGroup text={statementSection.text} /> : null}
            {statementSection.items?.length ? (
              <div className="mt-8 grid gap-4 md:grid-cols-2">
                {statementSection.items.map((item, index) => (
                  <div
                    key={`${item.slice(0, 28)}-${index}`}
                    className="rounded-[1.4rem] bg-white px-5 py-5 text-sm leading-7 text-slate-700"
                  >
                    {item}
                  </div>
                ))}
              </div>
            ) : null}
            {statementSection.note ? (
              <p className="mt-6 text-sm leading-7 text-slate-500">{statementSection.note}</p>
            ) : null}
          </div>
        </section>
      ) : null}

      {!leadershipSection && sidebarSection?.cards?.length ? (
        <section className="px-6 py-16 md:px-12 lg:px-20">
          <div className="mx-auto max-w-6xl">
            <SectionHeader label={sidebarSection.label} title={sidebarSection.title} />
            <div className="mt-10 grid gap-6 xl:grid-cols-2">
              <SidebarCards cards={sidebarSection.cards} />
            </div>
          </div>
        </section>
      ) : null}

      <ChecklistSection section={checklistSection} />
      <TimelineSection section={timelineSection} />
      <DirectoryCards section={directorySection} />

      {faqSection?.items?.length ? (
        <section className="px-6 py-16 md:px-12 lg:px-20">
          <div className="mx-auto max-w-6xl">
            <SectionHeader label={faqSection.label} title={faqSection.title} />
            <div className="mt-10 grid gap-6 lg:grid-cols-2">
              {faqSection.items.map((item, index) => (
                <article
                  key={`${item.question}-${index}`}
                  className="rounded-[1.75rem] border border-[#eadfc8] bg-[#fffdfa] p-6 shadow-[0_18px_50px_-34px_rgba(15,23,42,0.18)]"
                >
                  <h3 className="text-2xl text-slate-900">{item.question}</h3>
                  <TextGroup
                    text={item.answer}
                    className="text-sm leading-7 text-slate-600"
                    containerClassName="mt-3 space-y-3"
                  />
                </article>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {newsSection?.items?.length ? (
        <section className="px-6 pb-16 md:px-12 lg:px-20">
          <div className="mx-auto max-w-6xl">
            <SectionHeader label={newsSection.label} title={newsSection.title} />
            <div className="mt-10 grid gap-6 lg:grid-cols-3">
              {newsSection.items.map((item, index) => {
                const cardVisual = visualStory[index % visualStory.length];
                return (
                  <article
                    key={`${item.title}-${index}`}
                    className="overflow-hidden rounded-[1.75rem] border border-[#eadfc8] bg-[#fffdfa] shadow-[0_18px_50px_-34px_rgba(15,23,42,0.18)]"
                  >
                    <MediaHeader image={cardVisual.src} alt={cardVisual.alt} tag={cardVisual.tag} compact />
                    <div className="p-6">
                      <div className="flex flex-wrap items-center gap-3">
                        {item.tag ? (
                          <span className="inline-flex rounded-full bg-[#fff1cc] px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-[#8a6a33]">
                            {item.tag}
                          </span>
                        ) : null}
                        {item.meta ? (
                          <span className="text-xs font-medium uppercase tracking-[0.18em] text-slate-400">
                            {item.meta}
                          </span>
                        ) : null}
                      </div>
                      <h3 className="mt-5 text-xl text-slate-900">{item.title}</h3>
                      <TextGroup
                        text={item.text}
                        className="text-sm leading-7 text-slate-600"
                        containerClassName="mt-3 space-y-3"
                      />
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>
      ) : null}

      {stepsSection?.items?.length ? (
        <section className="bg-white px-6 py-16 md:px-12 lg:px-20">
          <div className="mx-auto max-w-6xl">
            <SectionHeader label={stepsSection.label} title={stepsSection.title} />
            <div className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
              {stepsSection.items.map((item, index) => {
                const cardVisual = visualStory[(index + 1) % visualStory.length];
                return (
                  <article
                    key={`${item.title}-${index}`}
                    className="overflow-hidden rounded-[1.75rem] border border-[#ebdfc8] bg-[#fbf7ef] shadow-[0_18px_50px_-34px_rgba(15,23,42,0.18)]"
                  >
                    <MediaHeader image={cardVisual.src} alt={cardVisual.alt} tag={cardVisual.tag} compact />
                    <div className="p-6">
                      <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#102046] text-sm font-semibold text-white">
                        {index + 1}
                      </div>
                      <h3 className="mt-5 text-2xl text-slate-900">{item.title}</h3>
                      <TextGroup
                        text={item.text}
                        className="text-sm leading-7 text-slate-600"
                        containerClassName="mt-3 space-y-3"
                      />
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>
      ) : null}

      {gallerySection?.images?.length ? (
        <section className="px-6 py-16 md:px-12 lg:px-20">
          <div className="mx-auto max-w-6xl">
            <SectionHeader label={gallerySection.label} title={gallerySection.title} />
            <div className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
              {gallerySection.images.map((image, index) => (
                <div
                  key={`${image.alt}-${index}`}
                  className={`group relative overflow-hidden rounded-[1.9rem] ${index === 0 ? "md:col-span-2 md:row-span-2" : ""}`}
                >
                  <img
                    src={image.src}
                    alt={image.alt}
                    className={`w-full object-cover transition duration-500 group-hover:scale-105 ${index === 0 ? "h-[420px]" : "h-[260px]"}`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-slate-950/10 to-transparent" />
                  <div className="absolute bottom-0 left-0 p-6">
                    <span className="inline-flex rounded-full bg-white/15 px-4 py-2 text-xs font-semibold uppercase tracking-[0.22em] text-white backdrop-blur-sm">
                      {image.tag}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {footerCta ? (
        <section className="px-6 pb-20 md:px-12 lg:px-20">
          <div className="mx-auto max-w-6xl rounded-[2rem] bg-[#102046] px-8 py-12 text-white shadow-[0_30px_80px_-40px_rgba(2,6,23,0.8)] md:px-12">
            <div className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr] lg:items-center">
              <div>
                <h2 className="text-3xl md:text-4xl">{footerCta.title}</h2>
                <TextGroup
                  text={footerCta.text}
                  className="text-base text-slate-200/80 md:text-lg"
                  containerClassName="mt-4 space-y-3"
                />
              </div>
              <div className="flex justify-start lg:justify-end">
                {footerCta.ctaLabel && (footerCta.ctaPath || footerCta.ctaHref) ? (
                  <ActionLink
                    action={{ label: footerCta.ctaLabel, path: footerCta.ctaPath, href: footerCta.ctaHref }}
                    variant="secondary"
                  />
                ) : null}
              </div>
            </div>
          </div>
        </section>
      ) : null}
    </>
  );
};

export default InfoPage;
